import { useEffect, useId, useState } from 'react';
import { Bell, BookX, Trash2, Minus, Plus, MessageSquare, Info, Pencil, StickyNote } from 'lucide-react';
import { clsx } from 'clsx';
import { CopyButton } from '@/components/ui/CopyButton';
import { isbnAnzeige, isbnEingabeFehler, isbnNackt } from '@/lib/isbn';
import { useVlbLookup } from '@/hooks/useVlbLookup';
import { datumKurzDe } from '@/lib/format';
import { PREIS_UNGUELTIG, centAnzeige, centZuEingabe, preisTextZuCent } from '@/lib/preis';
import { sendungsstandAnzeige, sendungsstandSatz } from '@/lib/sendungsstand';
import { Cover } from '@/components/ui/Cover';
import { useAuth } from '@/hooks/useAuth';
import { useDraft } from '@/hooks/useDraft';
import { DraftHinweis } from '@/components/ui/DraftHinweis';
import { ErwaehnungTextarea } from '@/components/ui/ErwaehnungTextarea';
import { BeschaffungSektion, type BeschaffungEingabe } from './BeschaffungSektion';
import type { DetailPosition } from '@/types/detail';
import { FOKUS_RING } from '@/lib/fokus';
import { useReserviertOeffnen } from '@/components/reserviert/ReserviertBlatt';
import { NachlieferungMarke } from '@/components/ui/NachlieferungMarke';
import { DigitalMarke } from '@/components/ui/DigitalMarke';

type Props = {
  position: DetailPosition;
  onMengeChange: (menge: number) => void;
  /** Titelkorrektur an einer von Hand erfassten Position (A1). */
  onTitelSave: (titel: string) => void;
  /** Autor nachtragen/ändern; `null` leert ihn (Befund 28.10.2026). */
  onAutorSave: (autor: string | null) => void;
  /**
   * ISBN an einer von Hand erfassten Position nachtragen, ändern oder
   * entfernen (E62). Nackte Ziffern; `null` entfernt sie.
   */
  onIsbnSave: (isbn: string | null) => void;
  onEingangChange: (eing: number) => void;
  onNotizSave: (notiz: string | null) => void;
  /** „Sagt mir Bescheid, wenn DIESER Titel da ist." (A56, #135) */
  onNachrichtWunsch: (wunsch: boolean) => void;
  /** Preis in Cent, `null` loescht ihn wieder (A12.3). */
  onPreisSave: (preisCent: number | null) => void;
  onBeschaffungAdd: (eintrag: BeschaffungEingabe) => void;
  onBeschaffungDelete: (id: number, beschriftung: string) => void;
  /** A60: unzugeordneten Eingang einem Weg zuschlagen. */
  onBeschaffungUmhaengen: (beschaffungId: number, menge: number) => void;
  onMeldungSave: (
    beschaffungId: number,
    meldung: string | null,
    meldungAm: string | null
  ) => void;
  /**
   * Vergriffene Menge setzen (Migration 035). 0 nimmt zurück — die
   * Rückfrage dafür (Tipp-Wand) stellt der Aufrufer.
   */
  onVergriffen: (menge: number) => void;
  onVergriffenZuruecknehmen: () => void;
  onDelete: () => void;
  canDelete: boolean;
  /**
   * Aus Shopify (E47, 02.11.2026): Titel, Preis und Menge führt der Shop,
   * löschen geht nicht. Eingang, Beschaffung, Meldungen, Notizen und
   * Vergriffen bleiben — das führt nur das Tracking. Der Server zieht
   * dieselbe Grenze (api/lib/shopsperre.php).
   */
  ausShop?: boolean;
};

export function PositionRow({
  position: p,
  onMengeChange,
  onTitelSave,
  onAutorSave,
  onIsbnSave,
  onEingangChange,
  onNotizSave,
  onNachrichtWunsch,
  onPreisSave,
  onBeschaffungAdd,
  onBeschaffungDelete,
  onBeschaffungUmhaengen,
  onMeldungSave,
  onVergriffen,
  onVergriffenZuruecknehmen,
  onDelete,
  canDelete,
  ausShop = false
}: Props) {
  const [vergriffenOffen, setVergriffenOffen] = useState(false);
  const [editNotiz, setEditNotiz] = useState(false);
  const [draft, setDraft] = useState(p.notiz ?? '');
  const [editTitel, setEditTitel] = useState(false);
  const [titelEntwurf, setTitelEntwurf] = useState(p.titel);
  const [autorEntwurf, setAutorEntwurf] = useState(p.autor ?? '');
  const auth = useAuth();

  // A1: Nur was ein Mensch eingetippt hat, darf ein Mensch korrigieren.
  // Ein Titel aus dem VLB ist ein Katalogwert; ihn hier zu überschreiben
  // hieße, eine zweite Wahrheit neben den Katalog zu legen
  // (Designprinzip 4). Der Server zieht dieselbe Grenze und antwortet
  // sonst mit 409 — die Sperre hier erspart nur den vergeblichen Weg.
  const titelAenderbar = p.ist_freitext && !ausShop;

  function titelUebernehmen(): void {
    const t = titelEntwurf.trim();
    // Ein leerer Titel ist keine Korrektur, sondern ein Verlust.
    if (t === '') return;
    if (t !== p.titel) onTitelSave(t);
    // Der Autor darf leer sein — ein Kalender hat keinen.
    const a = autorEntwurf.trim();
    if (a !== (p.autor ?? '')) onAutorSave(a === '' ? null : a);
    setEditTitel(false);
  }

  function bearbeitenOeffnen(): void {
    setTitelEntwurf(p.titel);
    setAutorEntwurf(p.autor ?? '');
    setEditTitel(true);
  }

  function bearbeitenAbbrechen(): void {
    setTitelEntwurf(p.titel);
    setAutorEntwurf(p.autor ?? '');
    setEditTitel(false);
  }

  // Auch die Positions-Notiz ist ein Verlustkandidat bei Sitzungsablauf (S-1).
  const entwurf = useDraft<string>({
    scope: `position-notiz-${p.id}`,
    userId: auth.data?.user.id ?? null,
    wert: draft,
    aktiv: editNotiz,
    istLeer: (w) => w.trim() === '' || w === (p.notiz ?? '')
  });
  const verlagUnbekannt = !p.ist_freitext && p.verlag_text !== null && p.verlag === null;

  // Merkzeichen in der Kopfzeile, wenn irgendein Beschaffungsweg dieser
  // Position eine Lieferantenmeldung trägt (E11a).
  //
  // Bisher hing das Zeichen nur am Chip weiter unten. Wer die Bestellung
  // überfliegt, sieht die Kopfzeile — dort gehört der Hinweis hin, dass
  // zu diesem Titel etwas gemeldet wurde. Als Sprechblase UND als
  // sichtbares Zeichen: auf einem Tablet gibt es kein Hover.
  const meldungen = p.beschaffungen.filter(
    (b) => b.meldung !== null && b.meldung !== ''
  );

  // A12.4: Wo steckt DIESE Position? Der Server hat die Frage schon
  // beantwortet, hier wird sie nur formuliert.
  const stand = sendungsstandAnzeige(p.sendungsstand, p.menge_wirksam);
  const drausssen = p.sendungsstand.art !== 'keine' && !stand?.imHaus;
  const loeschSperrGrund = sendungsstandSatz(p.sendungsstand) ?? 'ist Teil einer Sendung';

  const statusBadge = {
    voll: { label: '✓', cls: 'bg-teal-soft text-teal' },
    teil: { label: `${p.menge_eingegangen}/${p.menge_wirksam}`, cls: 'bg-gold-soft text-gold-dark' },
    offen: { label: '—', cls: 'bg-cream-dark text-ink-soft' },
    // Ein Wort statt eines Zeichens: „vergriffen" ist keine Zahl, die man
    // am Brett mitzählt, sondern eine Auskunft.
    vergriffen: { label: 'vergriffen', cls: 'bg-coral-soft text-coral' }
  }[p.status];

  return (
    // KEIN `opacity` fuer die schon versandten Positionen, obwohl es sich
    // anboete. Vier Gruende: durchscheinender Text ist auf einem Tablet im
    // Laden bei Tageslicht kaum noch zu lesen; der Kontrast faellt unter
    // das, was Sehschwaechere brauchen; „blass" liest sich wie „deaktiviert",
    // dabei ist die Zeile weiter bedienbar; und die Notiz-Flaeche darin
    // wuerde gleich mit verblassen, obwohl sie gerade dann zaehlt. Statt
    // dessen ein anderer, gleich heller Grund.
    <div
      className={clsx(
        'flex gap-3 rounded-md border border-line p-3',
        drausssen ? 'bg-cream-warm' : 'bg-cream'
      )}
    >
      {/* key: Wird die ISBN nachgetragen oder geändert, beginnt das
          Titelbild von vorn. Sonst bliebe ein „kein Bild" der alten
          Nummer an der neuen hängen (Cover merkt sich den Fehlschlag). */}
      <Cover key={p.isbn ?? ''} isbn={p.isbn} />

      {/* min-w-0 ist Pflicht, nicht Zierrat (K11-Falle an einem div):
          Ein Flex-Element hat `min-width: auto` und schrumpft deshalb
          nicht unter die Breite seines Inhalts. Seit die Meldungszeile
          ihren Text abschneidet statt umzubrechen (E15), ist dieser
          Inhalt beliebig breit — gemessen bei 390 px drückte er die
          Spalte auf 442 px auf und die Seite lief quer. */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-start gap-2">
          {editTitel ? (
            <input
              type="text"
              autoFocus
              value={titelEntwurf}
              maxLength={500}
              onChange={(e) => setTitelEntwurf(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  titelUebernehmen();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  bearbeitenAbbrechen();
                }
              }}
              aria-label="Titel der Position"
              className={`min-h-[44px] min-w-0 flex-1 rounded-md border border-line bg-cream px-3 py-2.5 font-serif text-sm text-ink ${FOKUS_RING}`}
            />
          ) : titelAenderbar ? (
            // Der Titel selbst ist der Knopf — auf einem Tablet trifft man
            // den Text leichter als ein Stift daneben. Das Stift-Symbol
            // steht trotzdem da: ohne Hover sieht sonst niemand, dass hier
            // etwas zu ändern ist.
            <button
              type="button"
              onClick={bearbeitenOeffnen}
              className="flex min-h-[44px] min-w-0 flex-1 items-center gap-1.5 text-left font-serif text-sm text-ink hover:text-akzent"
              aria-label={`Titel und Autor von „${p.titel}" ändern`}
            >
              <span className="min-w-0 break-words">{p.titel}</span>
              <Pencil aria-hidden className="mt-0.5 h-3 w-3 shrink-0 text-ink-faint" />
            </button>
          ) : (
            <p className="min-w-0 flex-1 break-words font-serif text-sm text-ink">{p.titel}</p>
          )}
          <span className={clsx('shrink-0 rounded px-2 py-0.5 text-xs tabular-nums', statusBadge.cls)}>
            {statusBadge.label}
          </span>
          {meldungen.length > 0 && (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded bg-gold-soft px-1.5 py-0.5 text-xs text-gold-dark"
              title={meldungen
                .map(
                  (b) =>
                    `${b.verlag_text ?? b.weg.label}` +
                    `${b.meldung_am !== null ? `, ${datumKurzDe(b.meldung_am)}` : ''}` +
                    `: ${b.meldung}`
                )
                .join(' · ')}
            >
              <Info aria-hidden className="h-3 w-3" />
              <span className="sr-only">Meldung des Lieferanten liegt vor</span>
              {meldungen.length > 1 && (
                <span className="tabular-nums">{meldungen.length}</span>
              )}
            </span>
          )}
          {/* E52: Teilsendung raus, diese Position kommt hinterher. */}
          {p.nachlieferung && <NachlieferungMarke art={p.nachlieferung} ebene="position" />}
          {p.digital && <DigitalMarke />}
          {p.ist_freitext && (
            /* E12: hiess „Freitext". Seit E9 kann eine handerfasste
               Position eine ISBN tragen — „Freitext" behauptete dann
               „ohne Nummer", und daneben stand die Nummer. Das Merkzeichen
               sagt, WOHER die Titeldaten kommen, nicht ob es eine ISBN
               gibt. */
            <span className="shrink-0 rounded bg-gold-soft px-2 py-0.5 text-xs text-gold-dark">
              von Hand
            </span>
          )}
        </div>
        {editTitel && (
          // Der Autor gehört zum selben Handgriff wie der Titel: Wer am
          // Tresen merkt, dass „Rudolf Steiner" im Titelfeld steht, will
          // beides in einem Zug richtigstellen.
          <input
            type="text"
            value={autorEntwurf}
            maxLength={255}
            placeholder="Autor (optional)"
            onChange={(e) => setAutorEntwurf(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                titelUebernehmen();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                bearbeitenAbbrechen();
              }
            }}
            aria-label="Autor der Position"
            className={`min-h-[44px] w-full min-w-0 rounded-md border border-line bg-cream px-3 py-2.5 text-sm text-ink ${FOKUS_RING}`}
          />
        )}
        {editTitel && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={titelUebernehmen}
              disabled={titelEntwurf.trim() === ''}
              className="inline-flex min-h-[44px] items-center text-xs text-akzent hover:text-akzent-stark disabled:text-ink-faint"
            >
              Speichern
            </button>
            <button
              type="button"
              onClick={bearbeitenAbbrechen}
              className="inline-flex min-h-[44px] items-center text-xs text-ink-soft hover:text-coral"
            >
              Abbrechen
            </button>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-faint">
          {(p.autor || p.verlag_text) && (
            <span className="min-w-0 break-words">
              {[p.autor, p.verlag_text].filter(Boolean).join(' · ')}
            </span>
          )}
          {ausShop ? (
            p.preis_cent !== null && (
              <span className="tabular-nums text-ink-soft">{centAnzeige(p.preis_cent)}</span>
            )
          ) : (
            <PreisFeld preisCent={p.preis_cent} onSave={onPreisSave} titel={p.titel} />
          )}
        </div>
        {titelAenderbar ? (
          <IsbnFeld isbn={p.isbn} titel={p.titel} onSave={onIsbnSave} />
        ) : p.isbn && (
          <div className="flex items-center gap-1 text-xs text-ink-faint">
            {/* Striche nur in der Anzeige — der Kopierknopf daneben
                gibt weiterhin die nackten Ziffern heraus, weil die in
                jedes Suchfeld und jede Bestellmaske passen. */}
            <span className="tabular-nums">ISBN {isbnAnzeige(p.isbn)}</span>
            <CopyButton value={p.isbn} label={`ISBN ${p.isbn} kopieren`} />
          </div>
        )}
        {p.isbn && p.andere && <TitelBlattKnopf isbn={p.isbn} andere={p.andere} />}
        {/* Ein nicht zugeordneter Verlag ist kein Fehler, sondern eine
            Randnotiz: die Position ist vollstaendig, nur die Zuordnung zur
            Verlagsliste fehlt. Deshalb dezent statt alarmrot — und ohne
            Verweis auf eine Einstellungsseite, die es noch nicht gibt
            (SPEC-008 steht aus). B62 */}
        {/* Migration 035: Vergriffen. Bei der ganzen Position sagt es
            schon das Merkzeichen oben; hier steht der Weg zurück — und bei
            einer Teilmenge, wie viele es betrifft. */}
        {p.menge_vergriffen > 0 && (
          <div className="flex flex-wrap items-center gap-x-2 text-xs text-coral">
            <span>
              {p.menge_vergriffen === p.menge
                ? 'Vergriffen — wird nicht beschafft.'
                : `${p.menge_vergriffen} von ${p.menge} vergriffen — nur ${p.menge_wirksam} werden beschafft.`}
            </span>
            <button
              type="button"
              onClick={onVergriffenZuruecknehmen}
              className="inline-flex min-h-[44px] items-center text-xs text-ink-soft underline-offset-2 hover:text-akzent hover:underline"
            >
              Zurücknehmen
            </button>
          </div>
        )}
        {vergriffenOffen && (
          <VergriffenFrage
            titel={p.titel}
            hoechstens={p.vergriffen_hoechstens}
            onAbbruch={() => setVergriffenOffen(false)}
            onSetzen={(m) => {
              setVergriffenOffen(false);
              onVergriffen(m);
            }}
          />
        )}
        {verlagUnbekannt && (
          <p className="text-xs text-ink-faint">
            Verlag „{p.verlag_text}" ist noch nicht in der Verlagsliste.
          </p>
        )}

        {editNotiz ? (
          <div className="mt-1 space-y-1">
            {entwurf.angebot !== null && (
              <DraftHinweis
                text="eine ungespeicherte Notiz"
                onWiederherstellen={() => {
                  const d = entwurf.annehmen();
                  if (d !== null) setDraft(d);
                }}
                onVerwerfen={entwurf.verwerfen}
              />
            )}
            {/* Dasselbe Feld wie bei Bestellnotiz und Kommunikation: @
                öffnet die Mitarbeiterliste. Wer erwähnt wird, liest der
                Server beim Speichern selbst aus dem Text — hier hängt die
                Erwähnung zusätzlich an DIESER Position, damit die Glocke
                das Buch nennen kann. */}
            <ErwaehnungTextarea
              value={draft}
              onChange={setDraft}
              rows={2}
              placeholder="Notiz zur Position — mit @ jemanden erwähnen"
              className="w-full rounded-md border border-line bg-cream px-2 py-1 text-sm"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  onNotizSave(draft.trim() === '' ? null : draft);
                  entwurf.loeschen();
                  setEditNotiz(false);
                }}
                className="inline-flex min-h-[44px] items-center text-xs text-akzent hover:text-akzent-stark"
              >
                Speichern
              </button>
              <button
                type="button"
                onClick={() => {
                  setDraft(p.notiz ?? '');
                  setEditNotiz(false);
                }}
                className="inline-flex min-h-[44px] items-center text-xs text-ink-soft hover:text-coral"
              >
                Abbrechen
              </button>
            </div>
          </div>
        ) : p.notiz ? (
          /* A9.3: Die Positions-Notiz trägt Sonderfälle, die den Vorgang
             ändern — „nur dieses eine Buch als Geschenk". Als kursiver
             grauer Text unter der Position war leicht zu übersehen, dass
             überhaupt eine da ist.

             Jetzt eine eigene Fläche mit Symbol und Rahmen in Gold: sie
             hebt sich vom Fließtext ab, ohne zu alarmieren — eine Notiz
             ist eine Mitteilung, kein Fehler. Der ganze Block bleibt
             anklickbar und trägt sein Berührziel, es gibt also nichts,
             was nur beim Überfahren erschiene (kein Hover-only). */
          <button
            type="button"
            onClick={() => { setDraft(p.notiz ?? ''); setEditNotiz(true); }}
            aria-label={`Notiz bearbeiten: ${p.notiz}`}
            className="flex w-full min-h-[44px] items-start gap-2 rounded-md border border-gold-dark/40 bg-gold-soft px-2.5 py-2 text-left text-sm text-ink hover:border-akzent"
          >
            <StickyNote aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-gold-dark" />
            <span className="min-w-0 whitespace-pre-wrap break-words">{p.notiz}</span>
          </button>
        ) : null}

        {/* Umbrechen statt quetschen: auf einem Telefon stehen Menge,
            Eingang und die beiden Icon-Knöpfe nicht nebeneinander in
            390 Pixeln. Vorher schrumpften sie, bis nichts mehr zu
            treffen war.

            Beschriftung und Zähler stecken je in einer eigenen Gruppe,
            sonst bricht die Zeile mitten zwischen ihnen um — beim ersten
            Anlauf stand „Eingang" am Ende der ersten Zeile und sein
            Zähler am Anfang der zweiten, direkt unter dem der Menge.
            Welche Zahl wozu gehörte, war nicht mehr zu sehen. */}
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-ink-soft">
          <span className="inline-flex items-center gap-2">
            <span>Menge</span>
            {/* Untergrenze ist das schon Eingetroffene, nicht fest 1 (A-02).
                Unter den Eingang gesenkt entstand "Bestellt 3/1" samt
                Haken, und der Eingang liess sich danach nur noch senken —
                der wahre Stand war nicht mehr abbildbar. Der Server lehnt
                das ohnehin ab; hier steht es, damit der Knopf gar nicht
                erst einlaedt. Massgeblich bleibt die Server-Pruefung. */}
            {ausShop ? (
              <span className="tabular-nums text-ink" title="Menge aus Shopify — dort ändern">
                {p.menge}
              </span>
            ) : (
              <Stepper
                was="Menge"
                value={p.menge}
                min={Math.max(1, p.menge_eingegangen)}
                max={999}
                onChange={onMengeChange}
              />
            )}
          </span>
          <span className="inline-flex items-center gap-2">
            <span>Eingang</span>
            <Stepper
              was="Eingang"
              value={p.menge_eingegangen}
              min={0}
              max={p.menge_wirksam}
              onChange={onEingangChange}
            />
          </span>

          {/* A56 (#135): derselbe Schalter wie im Erfassungs-Formular,
              an derselben Stelle — in der Reihe der kleinen Handgriffe.
              Wer den Wunsch beim Aufnehmen vergessen hat, traegt ihn
              hier nach; das ist am Telefon Alltag. */}
          <button
 data-nutzung="nachricht_wunsch"            type="button"
            onClick={() => onNachrichtWunsch(!p.nachricht_wunsch)}
            aria-pressed={p.nachricht_wunsch}
            className={clsx(
              'ml-auto inline-flex h-11 w-11 items-center justify-center rounded',
              p.nachricht_wunsch
                ? 'bg-akzent-sanft text-akzent-stark'
                : 'text-ink-faint hover:text-akzent'
            )}
            aria-label={
              p.nachricht_wunsch
                ? 'Nachricht gewünscht, wenn dieser Titel da ist — abwählen'
                : 'Nachricht gewünscht, wenn dieser Titel da ist'
            }
            title="Kundin wünscht Nachricht, sobald dieser Titel da ist"
          >
            <Bell className="h-4 w-4" />
          </button>

          {/* Nur solange noch nichts vergriffen ist und etwas offen sein
              kann: Was schon da ist, ist nicht vergriffen. Ist bereits
              etwas markiert, führt der Weg über „Zurücknehmen". */}
          {p.menge_vergriffen === 0 && p.vergriffen_hoechstens > 0 && !vergriffenOffen && (
            <button
 data-nutzung="vergriffen_markieren"              type="button"
              onClick={() => setVergriffenOffen(true)}
              className="inline-flex h-11 w-11 items-center justify-center rounded text-ink-faint hover:text-coral"
              aria-label={`„${p.titel}" als vergriffen markieren`}
              title="Als vergriffen markieren"
            >
              <BookX className="h-4 w-4" />
            </button>
          )}
          {!editNotiz && p.notiz === null && (
            <button
              type="button"
              onClick={() => setEditNotiz(true)}
              className="inline-flex h-11 w-11 items-center justify-center rounded text-ink-faint hover:text-akzent"
              aria-label="Notiz hinzufügen"
            >
              <MessageSquare className="h-4 w-4" />
            </button>
          )}
          {!ausShop && (
          <button
            type="button"
            onClick={onDelete}
            disabled={!canDelete}
            className={clsx(
              'inline-flex h-11 w-11 items-center justify-center rounded',
              canDelete
                ? 'text-ink-faint hover:bg-coral-soft hover:text-coral'
                : 'cursor-not-allowed text-ink-faint/50'
            )}
            // A12.5: Die Sperre nennt jetzt das Datum. Sie bleibt eine
            // Sperre und wird keine Rueckfrage — der Server weist das
            // Loeschen mit 409 ab, solange eine Sendung die Position
            // fuehrt; eine Rueckfrage bekaeme also nur ein "Nein" nach
            // dem "Ja". Warum der Knopf tot ist, steht daneben trotzdem
            // ohne Ueberfahren: das Merkzeichen in der Status-Zeile sagt
            // „Versendet 26.08.".
            aria-label={
              canDelete
                ? 'Position löschen'
                : `Nicht löschbar: „${p.titel}“ ${loeschSperrGrund}`
            }
            title={canDelete ? '' : `Nicht löschbar — ${loeschSperrGrund}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
          )}
        </div>

        <BeschaffungSektion
          position={p}
          onAdd={onBeschaffungAdd}
          onDelete={onBeschaffungDelete}
          onUmhaengen={onBeschaffungUmhaengen}
          onMeldungSave={onMeldungSave}
        />
      </div>
    </div>
  );
}

/**
 * Preis der Position, in Euro getippt, in Cent gespeichert (A12.3).
 *
 * LEER ZEIGT NICHTS
 *   Kein „0,00 €", wenn kein Preis erfasst ist. Ein Preis von null Euro
 *   ist eine Aussage (Werbeexemplar, Ersatzlieferung); „nicht erfasst"
 *   ist keine. Wer beides gleich anzeigt, macht aus dem Nichtwissen eine
 *   Behauptung. Der Server haelt denselben Unterschied: NULL bleibt NULL,
 *   0 bleibt 0.
 *
 *   Sichtbar ist trotzdem etwas — sonst gaebe es keinen Weg, einen Preis
 *   nachzutragen, ausser dem Ueberfahren mit der Maus, und Tablets haben
 *   kein Hover.
 */
function PreisFeld({
  preisCent,
  onSave,
  titel
}: {
  preisCent: number | null;
  onSave: (cent: number | null) => void;
  titel: string;
}) {
  const [offen, setOffen] = useState(false);
  const [text, setText] = useState(centZuEingabe(preisCent));
  const [fehler, setFehler] = useState<string | null>(null);

  function oeffnen(): void {
    setText(centZuEingabe(preisCent));
    setFehler(null);
    setOffen(true);
  }

  function uebernehmen(): void {
    const cent = preisTextZuCent(text);
    if (cent === PREIS_UNGUELTIG) {
      setFehler('Bitte einen Betrag wie 12,90 eingeben.');
      return;
    }
    if (cent !== preisCent) onSave(cent);
    setOffen(false);
  }

  if (!offen) {
    return (
      <button
        type="button"
        onClick={oeffnen}
        aria-label={
          preisCent === null
            ? `Preis für „${titel}" erfassen`
            : `Preis für „${titel}" ändern, derzeit ${centAnzeige(preisCent)}`
        }
        className="inline-flex min-h-[44px] items-center gap-1 rounded text-xs hover:text-akzent"
      >
        {preisCent === null ? (
          <span className="text-ink-faint">Preis erfassen</span>
        ) : (
          <span className="tabular-nums text-ink-soft">{centAnzeige(preisCent)}</span>
        )}
        <Pencil aria-hidden className="h-3 w-3 shrink-0 text-ink-faint" />
      </button>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <input
        type="text"
        autoFocus
        inputMode="decimal"
        value={text}
        maxLength={12}
        aria-label={`Preis für „${titel}" in Euro`}
        aria-invalid={fehler !== null}
        onChange={(e) => {
          setText(e.target.value);
          setFehler(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            uebernehmen();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            setOffen(false);
          }
        }}
        className={`min-h-[44px] w-28 rounded-md border border-line bg-cream px-2 py-1 text-right text-sm tabular-nums text-ink ${FOKUS_RING}`}
      />
      <span className="text-ink-faint">€</span>
      <button
        type="button"
        onClick={uebernehmen}
        className="inline-flex min-h-[44px] items-center text-xs text-akzent hover:text-akzent-stark"
      >
        Speichern
      </button>
      <button
        type="button"
        onClick={() => setOffen(false)}
        className="inline-flex min-h-[44px] items-center text-xs text-ink-soft hover:text-coral"
      >
        Abbrechen
      </button>
      {/* Leeren heisst loeschen — der Normalweg fuer „war ein Vertipper".
          Als eigener Knopf, damit niemand raten muss, ob ein leeres Feld
          speichert oder abbricht. */}
      {preisCent !== null && (
        <button
          type="button"
          onClick={() => setText('')}
          className="inline-flex min-h-[44px] items-center text-xs text-ink-soft hover:text-coral"
        >
          Leeren
        </button>
      )}
      {fehler !== null && <span className="w-full text-xs text-coral">{fehler}</span>}
    </span>
  );
}

/**
 * ISBN einer von Hand erfassten Position (E62, Betreiber 03.11.2026:
 * „die Möglichkeit, eine ISBN selbst einzutragen").
 *
 * EIGENER HANDGRIFF, NICHT IM TITEL-STIFT
 *   Die ISBN steht da, wo sie bei jeder anderen Position auch steht, und
 *   lässt sich dort eintragen — wie der Preis daneben („Preis erfassen").
 *   Im Titel-Stift wäre sie versteckt: Wer am Tablet eine Nummer
 *   nachtragen will, tippt nicht auf den Titel, und die Beschriftung
 *   „Titel und Autor ändern" verspräche sie nicht. Dazu kommt die
 *   Fehlermeldung: eine falsche Prüfziffer gehört an das ISBN-Feld, nicht
 *   unter ein Formular aus drei Feldern, von denen zwei in Ordnung sind.
 *
 * KEIN inputMode (Falle 18)
 *   `numeric` sperrt auf dem iPhone das X — und eine zehnstellige ISBN
 *   endet in jedem elften Fall darauf. Die E44-Ausnahme trug sich, weil
 *   es zur ISBN-10 die EAN-13 vom Buchrücken als zweiten Weg gibt. Hier
 *   gibt es den nicht: Nachgetragen wird gerade dann, wenn das Buch noch
 *   NICHT da ist und die Nummer aus BONUS oder einer Verlagsmail kommt.
 *   Ein Feld ohne inputMode ist unbequem; eines mit dem falschen ist
 *   kaputt.
 *
 * Titel, Autor und Preis rührt das Eintragen nicht an. Kennt das VLB die
 * Nummer doch, steht danach ein leiser Satz da — übernommen wird nichts.
 */
function IsbnFeld({
  isbn,
  titel,
  onSave
}: {
  isbn: string | null;
  titel: string;
  onSave: (isbn: string | null) => void;
}) {
  const [offen, setOffen] = useState(false);
  const [isbnText, setIsbnText] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  // Die Nummer, die gerade gespeichert wurde. Der VLB-Hinweis wird erst
  // gefragt, wenn der Server sie bestätigt hat (die Detailansicht lädt
  // nach dem Speichern neu) — sonst stünde nach einem abgewiesenen
  // Speichern ein Hinweis zu einer Nummer da, die es nicht gibt.
  const [angefragt, setAngefragt] = useState<string | null>(null);
  const vlb = useVlbLookup();
  const { mutate: vlbFragen } = vlb;
  const fehlerId = useId();

  useEffect(() => {
    if (angefragt !== null && isbn === angefragt) {
      setAngefragt(null);
      vlbFragen(angefragt);
    }
  }, [angefragt, isbn, vlbFragen]);

  function oeffnen(): void {
    setIsbnText(isbn === null ? '' : isbnAnzeige(isbn));
    setFehler(null);
    setOffen(true);
  }

  function uebernehmen(): void {
    if (isbnText.trim() === '') {
      // Leer gespeichert heißt entfernt — derselbe Weg wie „Entfernen".
      if (isbn !== null) onSave(null);
      setOffen(false);
      return;
    }
    const f = isbnEingabeFehler(isbnText);
    if (f !== null) {
      setFehler(f);
      return;
    }
    const nackt = isbnNackt(isbnText);
    if (nackt !== isbn) {
      onSave(nackt);
      setAngefragt(nackt);
    }
    setOffen(false);
  }

  if (!offen) {
    // Der Hinweis gilt nur der Nummer, nach der gefragt wurde. Ist sie
    // inzwischen geändert oder entfernt, schweigt er.
    const vlbTreffer =
      vlb.isSuccess && isbn !== null && vlb.variables === isbn ? vlb.data.titel : null;
    return (
      <div className="flex flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-1 text-xs text-ink-faint">
          <button
            type="button"
            onClick={oeffnen}
            aria-label={
              isbn === null
                ? `ISBN für „${titel}" eintragen`
                : `ISBN von „${titel}" ändern, derzeit ${isbnAnzeige(isbn)}`
            }
            className="inline-flex min-h-[44px] items-center gap-1 rounded text-xs hover:text-akzent"
          >
            {isbn === null ? (
              <span>ISBN eintragen</span>
            ) : (
              <span className="tabular-nums">ISBN {isbnAnzeige(isbn)}</span>
            )}
            <Pencil aria-hidden className="h-3 w-3 shrink-0" />
          </button>
          {isbn !== null && <CopyButton value={isbn} label={`ISBN ${isbn} kopieren`} />}
        </div>
        {vlbTreffer !== null && (
          <p className="flex items-start gap-1 text-xs text-ink-soft">
            <Info aria-hidden className="mt-0.5 h-3 w-3 shrink-0" />
            <span>
              Das VLB kennt diese ISBN: „{vlbTreffer.titel}"
              {vlbTreffer.autor ? ` von ${vlbTreffer.autor}` : ''}. Übernommen wird nichts —
              Titel und Preis hier bleiben, wie sie sind.
            </span>
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <input
        type="text"
        autoFocus
        value={isbnText}
        maxLength={20}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        placeholder="z. B. 978-3-…"
        aria-label={`ISBN für „${titel}"`}
        aria-invalid={fehler !== null}
        aria-describedby={fehler !== null ? fehlerId : undefined}
        onChange={(e) => {
          setIsbnText(e.target.value);
          setFehler(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            uebernehmen();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            setOffen(false);
          }
        }}
        className={`min-h-[44px] w-52 min-w-0 rounded-md border border-line bg-cream px-2 py-1 text-sm tabular-nums text-ink ${FOKUS_RING}`}
      />
      <button
        type="button"
        onClick={uebernehmen}
        className="inline-flex min-h-[44px] items-center text-xs text-akzent hover:text-akzent-stark"
      >
        Speichern
      </button>
      <button
        type="button"
        onClick={() => setOffen(false)}
        className="inline-flex min-h-[44px] items-center text-xs text-ink-soft hover:text-coral"
      >
        Abbrechen
      </button>
      {/* Entfernen als eigener Knopf, wie „Leeren" beim Preis: niemand
          soll raten müssen, ob ein leeres Feld speichert oder abbricht. */}
      {isbn !== null && (
        <button
          type="button"
          onClick={() => {
            onSave(null);
            setOffen(false);
          }}
          className="inline-flex min-h-[44px] items-center text-xs text-ink-soft hover:text-coral"
        >
          Entfernen
        </button>
      )}
      {fehler !== null && (
        <span id={fehlerId} role="alert" className="w-full text-xs text-coral">
          {fehler}
        </span>
      )}
    </div>
  );
}

/**
 * Rückfrage „vergriffen?" an der Zeile (Migration 035).
 *
 * Der Normalfall ist die ganze Position — ein Tipp. Eine Teilmenge ist
 * selten und deshalb hinter „Nur einen Teil …" versteckt, aber möglich.
 * Die Zeile selbst ist die Bestätigung; ein Dialog darüber wäre ein
 * zweiter Klick für dieselbe Frage.
 */
function VergriffenFrage({
  titel,
  hoechstens,
  onAbbruch,
  onSetzen
}: {
  titel: string;
  hoechstens: number;
  onAbbruch: () => void;
  onSetzen: (menge: number) => void;
}) {
  const [teil, setTeil] = useState(false);
  const [anzahl, setAnzahl] = useState(1);
  return (
    <div
      role="group"
      aria-label={`„${titel}" als vergriffen markieren`}
      className="mt-1 space-y-2 rounded-md border border-coral/40 bg-coral-soft/40 px-3 py-2 text-xs text-ink"
    >
      <p>
        {hoechstens === 1
          ? 'Ist dieser Titel vergriffen? Er wird dann nicht mehr beschafft und hält die Bestellung nicht auf.'
          : `Sind alle ${hoechstens} offenen Exemplare vergriffen? Sie werden dann nicht mehr beschafft.`}
      </p>
      {teil ? (
        <div className="flex flex-wrap items-center gap-2">
          <span>Vergriffen:</span>
          <Stepper was="Vergriffen" value={anzahl} min={1} max={hoechstens} onChange={setAnzahl} />
          <span>von {hoechstens}</span>
          <button
            type="button"
            onClick={() => onSetzen(anzahl)}
            className="inline-flex min-h-[44px] items-center rounded-md bg-coral px-3 text-xs text-cream hover:bg-coral/90"
          >
            Übernehmen
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => onSetzen(hoechstens)}
            className="inline-flex min-h-[44px] items-center rounded-md bg-coral px-3 text-xs text-cream hover:bg-coral/90"
          >
            {hoechstens === 1 ? 'Als vergriffen markieren' : `Alle ${hoechstens} als vergriffen markieren`}
          </button>
          {hoechstens > 1 && (
            <button
              type="button"
              onClick={() => setTeil(true)}
              className="inline-flex min-h-[44px] items-center text-xs text-ink-soft underline-offset-2 hover:underline"
            >
              Nur einen Teil …
            </button>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={onAbbruch}
        className="inline-flex min-h-[44px] items-center text-xs text-ink-soft hover:text-ink"
      >
        Abbrechen
      </button>
    </div>
  );
}

// `was` benennt, was hier hoch- und runtergezaehlt wird. Die beiden
// Knoepfe trugen nur ein Symbol und hiessen fuer Screenreader schlicht
// "Schaltflaeche" — nebeneinander viermal dasselbe. Beim Durchspielen der
// Einfuehrungstour fiel auf, dass sich der Eingangs-Knopf deshalb auch
// nicht benennen liess.
function Stepper({
  was,
  value,
  min,
  max,
  onChange
}: {
  was: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="inline-flex items-center rounded-md border border-line">
      <button
        type="button"
        aria-label={`${was} verringern`}
        onClick={() => onChange(Math.max(min, value - 1))}
        // Echte 44 Pixel statt einer unsichtbaren Trefferfläche: die
        // beiden Knöpfe stoßen aneinander, überlappende Flächen würden
        // beim Zählen den falschen treffen.
        className="inline-flex h-11 w-11 items-center justify-center text-ink-soft hover:bg-cream-dark"
        disabled={value <= min}
      >
        <Minus className="h-3 w-3" />
      </button>
      <span className="w-8 text-center text-sm tabular-nums">{value}</span>
      <button
        type="button"
        aria-label={`${was} erhöhen`}
        onClick={() => onChange(Math.min(max, value + 1))}
        className="inline-flex h-11 w-11 items-center justify-center text-ink-soft hover:bg-cream-dark"
        disabled={value >= max}
      >
        <Plus className="h-3 w-3" />
      </button>
    </div>
  );
}

/**
 * Das Titel-Blatt (02.11.2026, Skizze „Ablage und Titel-Blatt"): Wartet
 * noch jemand anderes auf diesen Titel, und liegt dafür schon etwas im
 * Regal? Ein Tipp öffnet das Reserviert-Blatt, schon mit der ISBN gefragt.
 *
 * ABWEICHUNG VON DER SKIZZE: Dort stand auch bei „nur hier" ein Knopf.
 * Hier steht dann nur ein leiser Satz — ein Knopf an jeder Position, der
 * ein Blatt mit „Nein" öffnet, wäre Lärm an der Stelle, an der am
 * meisten gearbeitet wird.
 */
function TitelBlattKnopf({
  isbn,
  andere
}: {
  isbn: string;
  andere: { bestellungen: number; warten: number; im_regal: number };
}) {
  const oeffnen = useReserviertOeffnen();
  if (andere.bestellungen === 0) {
    return <p className="text-xs text-ink-faint">Dieser Titel: nur in dieser Bestellung</p>;
  }
  const teile = [
    `${andere.bestellungen} ${andere.bestellungen === 1 ? 'weitere Bestellung' : 'weitere Bestellungen'}`,
    andere.im_regal > 0 ? `${andere.im_regal} im Regal` : null
  ].filter(Boolean);
  return (
    <button
      type="button"
      onClick={() => oeffnen(isbn)}
      className="inline-flex min-h-[44px] w-fit items-center gap-1.5 rounded-full border border-gold px-3 text-xs text-gold-dark hover:bg-gold-soft"
      title="Öffnet „Reserviert?“ mit dieser ISBN"
    >
      Dieser Titel: {teile.join(' · ')}
    </button>
  );
}
